import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AsyncPipe, NgClass } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { Observable, combineLatest, share } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InAppNotificationType } from 'src/app/in-app-notifications/models/in-app-notification-type.enum';
import { InAppNotification } from 'src/app/in-app-notifications/models/in-app-notification.model';
import {
    getInAppNotifications,
    getInAppNotificationById,
    updateInAppNotificationSettings,
    markAllAsRead,
    resetState,
    getUnreadInAppNotificationsCount,
    hasOverdueTasksCreatedByMe,
    getUnreadInAppNotificationsCountForDelegation
} from 'src/app/in-app-notifications/store/actions/in-app-notifications.actions';
import {
    selectInAppNotifications,
    selectLoading,
    selectInAppNotificationSentAlert,
    selectUnreadInAppNotificationCounts,
    selectHasOverdueTasksCreatedByMe,
    selectUnreadInAppNotificationCountsForDelegation
} from 'src/app/in-app-notifications/store/selectors/in-app-notifications.selectors';
import { InAppNotificationsState } from 'src/app/in-app-notifications/store/in-app-notifications.state';
import { IdentityService } from 'src/app/core/identity.service';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { CorporateDirectoryUser } from 'src/app/corporate-directory/models/corporate-directory-user.model';
import { TimeAgoPipe } from 'src/app/shared/pipes/time-ago.pipe';
import { CardComponent } from 'src/app/shared/components/nova-card/nova-card.component';

@UntilDestroy()
@Component({
    selector: 'nova-in-app-notifications-list',
    standalone: true,
    imports: [
        MatProgressBarModule,
        MatIconModule,
        MatCardModule,
        MatSlideToggleModule,
        MatListModule,
        MatTooltipModule,
        TimeAgoPipe,
        AsyncPipe,
        NgClass,
        RouterModule,
        CardComponent
    ],
    templateUrl: './in-app-notifications-list.component.html',
    styleUrls: ['./in-app-notifications-list.component.scss']
})
export class InAppNotificationsListComponent implements OnInit {
    readonly InAppNotificationType = InAppNotificationType;
    readonly onlyShowUnreadStorageKey = 'InAppNotifications.onlyShowUnread';
    aspNetUserId: string;
    currentUserId: string;

    featureFlag = FeatureFlag;
    isLoading$: Observable<boolean>;
    inAppNotifications: InAppNotification[] = [];
    onlyShowUnread = true;
    overdueTasksAssignedToMeCount = 0;
    hasOverdueTasksCreatedByMe = false;
    enableInAppNotificationsTickets = false;

    @Input()
    parentInAppNotificationTypeId!: InAppNotificationType;

    @Input()
    inAppNotificationUser?: CorporateDirectoryUser;

    constructor(
        private readonly _store: Store<InAppNotificationsState>,
        private readonly _router: Router,
        private readonly _identityService: IdentityService,
        private readonly _ldFeatureManager: LDFeatureManager,
        private readonly _activatedRoute: ActivatedRoute
    ) {}

    get hasAnyUnread(): boolean {
        return this.inAppNotifications.filter((x) => !x.isRead).length === 0;
    }

    get overdueTasksAssignedToMeMessage(): string {
        if (this.overdueTasksAssignedToMeCount > 1) {
            return `${this.overdueTasksAssignedToMeCount} tasks assigned to you are overdue.`;
        } else if (this.overdueTasksAssignedToMeCount === 1) {
            return `${this.overdueTasksAssignedToMeCount} task assigned to you is overdue.`;
        }

        return `You have no overdue tasks assigned to you; you're a superstar!`;
    }

    get userOnlyShowUnreadSessionStorageKey(): string {
        return `${this.onlyShowUnreadStorageKey}.${this.aspNetUserId}`;
    }

    ngOnInit(): void {
        this.currentUserId = this._identityService.current.userId;

        this.isLoading$ = this._store.select(selectLoading);

        this._store
            .select(selectInAppNotifications)
            .pipe(untilDestroyed(this))
            .subscribe((data) => (this.inAppNotifications = data));

        this._ldFeatureManager
            .isEnabled(this.featureFlag.InAppNotificationsTickets)
            .pipe(untilDestroyed(this))
            .subscribe((enabled) => {
                this.enableInAppNotificationsTickets = enabled;
            });

        this.setupInAppNotificationData();

        this._store
            .select(selectInAppNotificationSentAlert)
            .pipe(untilDestroyed(this))
            .subscribe((data) => {
                if (
                    data &&
                    data.parentInAppNotificationTypeId === this.parentInAppNotificationTypeId &&
                    data.aspNetUserIds.includes(this.aspNetUserId) &&
                    this.inAppNotifications.findIndex((x) => x.id === data.notificationId) < 0
                ) {
                    this._store.dispatch(
                        getInAppNotificationById({
                            notificationId: data.notificationId
                        })
                    );
                }
            });
    }

    private setupInAppNotificationData() {
        this._activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((queryParams) => {
            const newUserId = queryParams.userId;
            if (!this.aspNetUserId || newUserId !== this.aspNetUserId) {
                this.loadData(newUserId ?? this.currentUserId);
            }

            combineLatest([
                this._store.select(selectUnreadInAppNotificationCounts),
                this._store.select(selectUnreadInAppNotificationCountsForDelegation)
            ])
                .pipe(share(), untilDestroyed(this))
                .subscribe(([unreadInAppNotificationCounts, unreadInAppNotificationCountsForDelegation]) => {
                    this.overdueTasksAssignedToMeCount = this.isDelegatee
                        ? unreadInAppNotificationCountsForDelegation.overdueTasksAssignedToMeCount
                        : unreadInAppNotificationCounts.overdueTasksAssignedToMeCount;
                });
        });
    }

    loadData(userId: string) {
        this.aspNetUserId = userId ?? this.currentUserId;

        const onlyShowUnreadValue = sessionStorage.getItem(this.userOnlyShowUnreadSessionStorageKey);
        if (onlyShowUnreadValue !== null) {
            this.onlyShowUnread = onlyShowUnreadValue === 'true';
        } else {
            this.onlyShowUnread = true;
        }
        this._store.dispatch(resetState());
        this.getInAppNotifications();

        if (this.isDelegatee) {
            this._store.dispatch(getUnreadInAppNotificationsCountForDelegation({ userId: this.aspNetUserId }));
        } else {
            this._store.dispatch(getUnreadInAppNotificationsCount());
        }

        if (this.parentInAppNotificationTypeId === InAppNotificationType.Tasks && !this.isDelegatee) {
            this._store.dispatch(hasOverdueTasksCreatedByMe());
            this._store
                .select(selectHasOverdueTasksCreatedByMe)
                .pipe(share(), untilDestroyed(this))
                .subscribe((hasOverdueTasksCreatedByMe) => {
                    this.hasOverdueTasksCreatedByMe = hasOverdueTasksCreatedByMe;
                });
        }
    }

    markAllAsRead($event: Event) {
        $event.preventDefault();
        this._store.dispatch(
            markAllAsRead({
                parentInAppNotificationTypeId: this.parentInAppNotificationTypeId,
                userId: this.aspNetUserId
            })
        );
    }

    toggleRead(notificationId: number) {
        const isRead = this.inAppNotifications.find((x) => x.id === notificationId)?.isRead;
        this._store.dispatch(
            updateInAppNotificationSettings({
                notificationId,
                userId: this.aspNetUserId,
                settings: {
                    isRead: !isRead
                }
            })
        );
    }

    toggleOnlyShowUnread() {
        this.onlyShowUnread = !this.onlyShowUnread;
        sessionStorage.setItem(this.userOnlyShowUnreadSessionStorageKey, this.onlyShowUnread.toString());
        this.getInAppNotifications();
    }

    navigateToNotificationPath(inAppNotification: InAppNotification) {
        this.markAsReadIfUnread(inAppNotification);

        if (inAppNotification.path) {
            this._router.navigate([inAppNotification.path]);
        }
    }

    markAsReadIfUnread(inAppNotification: InAppNotification) {
        const isRead = inAppNotification.isRead;
        if (!isRead) {
            this.toggleRead(inAppNotification.id);
        }
    }

    get isDelegatee() {
        return this.currentUserId !== this.aspNetUserId;
    }

    private getInAppNotifications() {
        this._store.dispatch(
            getInAppNotifications({
                filter: {
                    parentInAppNotificationTypeId: this.parentInAppNotificationTypeId,
                    onlyUnread: this.onlyShowUnread,
                    userId: this.aspNetUserId
                }
            })
        );
    }
}
